//! Essential imports
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

//! Style imports
import styled from 'styled-components';
import * as styles from "../../styles/MonitoringPage";

//! library imports
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { confirmAlert } from "react-confirm-alert";
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from "dayjs";



type MonitoringType = {
	children: Array<MonitoringType>; // 더 구체적인 타입으로 변경 가능
	delDate: string | null;
	equips: Array<EquipType>; // 더 구체적인 타입으로 변경 가능
	gateways: Array<GatewayType>; // 더 구체적인 타입으로 변경 가능
	modDate: string;
	name: string;
	placeId: number;
	regDate: string;
	type: string;
};

type EquipType = {
	"equipId": number;
	"name": string;
	"seq": number;
	"location": string;
	"order": number;
	"equipClass": EquipClassType,
	"sensors"?: Array<SensorType>,
	"actuators"?: Array<ActuatorType>,
};

type EquipClassType = {
	"equipClassId": number;
	"name": string;
	"code": string;
	"equipProtocolMap": any;
	"enableControlYn": string;
	"enableAutoYn": string;
};

type GatewayType = any;

type SensorType = {
	tagId: number;
	name: string;
	value: number;
	driveYn: string | null;
	seq: number;
	bitSeq: string | null;
	regDate: string;
	modDate: string;
	normalYn: string;
	gatewayMap: any;
	tagClass: any;
};


type ActuatorType = {
	bitSeq: string | null;
	driveYn: string | null;
	gatewayMap: any;
	modDate: string;
	name: string;
	normalYn: string;
	regDate: string;
	seq: number;
	tagClass: any;
	tagId: number;
	value: number;
};

// actuator 데이터 업데이트를 위한 Type
type ActuatorUpdateType = {
	equipId: number | undefined;
	equipClassCode: string | undefined;
	equipClassValue: string | undefined;
	params: Array<ActuatorUpdateParamsType>;
};

type ActuatorUpdateParamsType = {
	id: number | undefined;
	classCode: string | undefined;
	address: number | undefined;
	encoding: string | undefined;
	value: number | undefined;
	seq: number | undefined;
};

const MonitoringPage = () => {
	const navigate = useNavigate();
	const targetFarmInfo = JSON.parse(localStorage.getItem("targetfarmInfo") || "{}");

	//! State
	const [monitoringData, setMonitoringData] = useState<MonitoringType[]>([]);
	const [selectedPlaceIds, setSelectedPlaceIds] = useState<(number | null)[]>([]);
	// actuator 데이터 업데이트를 위한 
	const [actuatorUpdateData, setActuatorUpdateData] = useState<ActuatorUpdateType>({
		equipId: 0,
		equipClassCode: "",
		equipClassValue: "",
		params: [],
	});
	const [isLoading, setIsLoading] = useState<(number | null)>(null);
	// 갱신 날짜 시간 표시 state
	const [updateTime, setUpdateTime] = useState<string>("");


	//! API
	// 모니터링 정보 API
	const getMonitoring = useCallback(async () => {
		//ifactory/api/place/placetag/:placeId  (GET)								
		try {
			const response = await axios.get(`ifactory/api/place/placetag/${targetFarmInfo.placeId}`);
			return response.data;
		} catch (error: any) {
			console.error(error);
		}
	}, [targetFarmInfo]);

	// actuator Update API
	const updateActuator = useCallback(async () => {
		if (!actuatorUpdateData.equipId) return;
		if (!actuatorUpdateData.params) return;
		if (actuatorUpdateData.params.length === 0) return;
		if (actuatorUpdateData.params[0].id === undefined) return;

		// ifactory/api/control  (POST)						
		try {
			setIsLoading(actuatorUpdateData.params[0].id);
			return await axios.post(`ifactory/api/control`, actuatorUpdateData);
		} catch (error: any) {
			return {
				data: 'error',
			}
		}

	}, [actuatorUpdateData]);


	//! React Hooks
	useEffect(() => {
		const fetchMonitoringData = async () => {
			const data = await getMonitoring();
			setMonitoringData([data]);

			const now = dayjs().format('YYYY-MM-DD HH:mm:ss'); // 현재 시간을 'YYYY-MM-DD HH:mm:ss' 형식으로 포맷팅합니다.
			setUpdateTime(now);
		};

		fetchMonitoringData(); // 처음 컴포넌트가 마운트될 때 데이터를 가져옵니다.

		const intervalId = setInterval(fetchMonitoringData, 15000); // 15분마다 데이터를 갱신합니다.

		return () => clearInterval(intervalId); // 컴포넌트가 언마운트될 때 인터벌을 정리합니다.
		// eslint-disable-next-line
	}, []);

	//! Fn
	const findDataByPlaceId = (data: MonitoringType[], targetPlaceId: number): MonitoringType | null => {
		for (const item of data) {
			// 현재 데이터의 placeId가 찾고자 하는 placeId와 일치하는지 확인
			if (item.placeId === targetPlaceId) {
				return item;
			}

			// 현재 데이터의 children 배열을 재귀적으로 탐색하여 찾고자 하는 placeId를 찾음
			if (item.children) {
				const result = findDataByPlaceId(item.children, targetPlaceId);
				if (result) {
					return result;
				}
			}
		}

		// 해당 placeId를 찾지 못한 경우 null 반환
		return null;
	};

	const handleClick = (placeId: number) => {
		if (monitoringData.length === 0) return;

		setSelectedPlaceIds(prev => {
			const index = prev.indexOf(placeId);
			if (index === -1) {
				return [...prev, placeId];
			} else {
				return prev.filter((id) => id !== placeId);
			}
		});
	};

	const [open, setOpen] = useState(false);
	const handleOpen = (actuator?: ActuatorType[]) => {
		if (!actuator) return;
		if (actuator.length === 0) return;

		setSelectedActuatorList(actuator);
		setOpen(true);
	};

	const handleClose = () => {
		setSelectedActuatorList([]);
		setOpen(false);
	};

	// actuator target
	const [selectedActuatorList, setSelectedActuatorList] = useState<ActuatorType[]>([]);

	// actuator 클릭 이벤트
	const handleActuatorClick = (equip?: EquipType): React.MouseEventHandler<HTMLLIElement> => (event) => {
		if (equip?.actuators?.length === 0) {
			confirmAlert({
				title: "제어할 수 있는 장비가 없습니다.",
				buttons: [{ label: "확인" }],
			});
			return;
		};

		if (open) return;

		// 모달을 열기전 update를 위한 데이터 세팅
		setActuatorUpdateData({
			equipId: equip?.equipId,
			equipClassCode: equip?.equipClass.code,
			equipClassValue: equip?.equipClass.equipProtocolMap,
			params: [
				{
					id: undefined,
					classCode: undefined,
					address: undefined,
					encoding: undefined,
					value: undefined,
					seq: equip?.seq,
				},
			],
		});
		handleOpen(equip?.actuators);
	};

	// 환경설정
	const renderActuators = (equip: EquipType, firstActuatorEquipId: number | undefined) => {
		return (
			<div>
				{equip.equipId === firstActuatorEquipId && (<div className={`equip-tit actuator-tit`}>제어장치</div>)}

				<ul className="acc-actuator">
					{
						<li className="acc-actuator-list" onClick={handleActuatorClick(equip)}>
							<div className="actuator-con">
								{equip.name}
							</div>
						</li>
					}
				</ul>
			</div>		);
	};

	// 냉난방기
	const renderTemp = (equip: EquipType, firstActuatorEquipId: number | undefined) => {
		return (
			<div>
				{equip.equipId === firstActuatorEquipId && (<div className={`equip-tit actuator-tit`}>제어장치</div>)}

				<ul className="acc-actuator">
					{
						<li className="acc-actuator-list" onClick={handleActuatorClick(equip)}>
							<div className="actuator-aircon">
								{equip.name}
							</div>
						</li>
					}
				</ul>
			</div>		);
	};

		// 가습기
		const renderHumid = (equip: EquipType, firstActuatorEquipId: number | undefined) => {
			return (
				<div>
					{equip.equipId === firstActuatorEquipId && (<div className={`equip-tit actuator-tit`}>제어장치</div>)}
	
					<ul className="acc-actuator">
						{
							<li className="acc-actuator-list" onClick={handleActuatorClick(equip)}>
								<div className="actuator-humid">
									{equip.name}
								</div>
							</li>
						}
					</ul>
				</div>		);
		};

		// 전열교환기
		const renderHeat = (equip: EquipType, firstActuatorEquipId: number | undefined) => {
			return (
				<div>
					{equip.equipId === firstActuatorEquipId && (<div className={`equip-tit actuator-tit`}>제어장치</div>)}

					<ul className="acc-actuator">
						{
							<li className="acc-actuator-list" onClick={handleActuatorClick(equip)}>
								<div className="actuator-heat">
									{equip.name}
								</div>
							</li>
						}
					</ul>
				</div>		);
		};

		// 조명
		const renderLight = (equip: EquipType, firstActuatorEquipId: number | undefined) => {
			return (
				<div>
					{equip.equipId === firstActuatorEquipId && (<div className={`equip-tit actuator-tit`}>제어장치</div>)}
	
					<ul className="acc-actuator">
						{
							<li className="acc-actuator-list" onClick={handleActuatorClick(equip)}>
								<div className="actuator-light">
									{equip.name}
								</div>
							</li>
						}
					</ul>
				</div>		);
		};

		// Fan
		const renderFan = (equip: EquipType, firstActuatorEquipId: number | undefined) => {
			return (
				<div>
					{equip.equipId === firstActuatorEquipId && (<div className={`equip-tit actuator-tit`}>제어장치</div>)}
	
					<ul className="acc-actuator">
						{
							<li className="acc-actuator-list" onClick={handleActuatorClick(equip)}>
								<div className="actuator-fan">
									{equip.name}
								</div>
							</li>
						}
					</ul>
				</div>		);
		};

	const renderSensors = (equip: EquipType) => {
		return (
			<div>
				<div className={`equip-tit`}>{equip.name}</div>
				<ul className="acc-sensor">
					{
						equip.sensors && equip.sensors.map((sensor: SensorType, index: number) => (

							<li key={sensor.tagId} className="acc-sensor-list">
								{
									sensor.normalYn === 'Y'
										? (<>
											<div className="sensor-tit normal-y" >{sensor.name}</div>
											<div className="sensor-val">
												<span className="value">{sensor.value} <span className="unit">{sensor.tagClass.unit}</span></span>
											</div>
										</>)
										: (<>
											<div className="sensor-tit normal-n">{sensor.name}</div>
											<div className="sensor-val">
												<span className="value">통신오류</span>
											</div>
										</>)
								}
							</li>
						))
					}
				</ul>
			</div>
		);
	};


	const renderEquips = (equips: Array<EquipType>) => {
		if (equips.length === 0) return (<div className="acc-equip"><span className="no-data-txt">해당 정보가 없습니다.</span></div>);

		const firstActuatorEquip = equips.find(equip => equip.equipClass.enableControlYn === 'Y');
		const firstActuatorEquipId = firstActuatorEquip ? firstActuatorEquip.equipId : undefined;

		return (
			<ul className="acc-equip">
				{equips.map((equip: EquipType, index: number) => (
					<li key={equip.equipId} className={`acc-equip-list ${equip.equipClass.enableControlYn === 'N' ? 'sensor-group' : 'actualtor-group'}`}>
						{
							// equip.equipClass.enableControlYn === 'N' ?
							// 	equip.sensors && renderSensors(equip)
							// 	: equip.actuators && renderActuators(equip, firstActuatorEquipId)
							equip.equipClass.enableControlYn === 'N' ? (
								equip.sensors && renderSensors(equip)
							) : (
								equip.equipClass.code.includes('AIRCON') ? renderTemp(equip, firstActuatorEquipId) :
								equip.equipClass.code.includes('HUMID') ? renderHumid(equip, firstActuatorEquipId) :
								equip.equipClass.code.includes('HEAT') ? renderHeat(equip, firstActuatorEquipId) :
								equip.equipClass.code.includes('LIGHT') ? renderLight(equip, firstActuatorEquipId) :
								equip.equipClass.code.includes('FAN') ? renderFan(equip, firstActuatorEquipId) :
								equip.actuators && renderActuators(equip, firstActuatorEquipId)
							)
						}
					</li>
				))}
			</ul>
		);
	};

	// equips에 데이터가 존재하는지 검증
	const checkForRenderPlaces = (children: MonitoringType[]) => {
		return (
			<React.Fragment>
				{children.map((child, index) => {
					if (child.equips && child.equips.length > 0) {
						return (
							<div key={child.placeId} className="acc-child" style={{ marginLeft: '20px' }}>
								<React.Fragment>{renderPlaces([child], index)}</React.Fragment>
							</div>
						)
					} else {
						return <React.Fragment key={index}>{checkForRenderPlaces(child.children)}</React.Fragment>;
					}
				})}
			</React.Fragment>
		);
	};

	// place DOM 생성
	const renderPlaces = (children: MonitoringType[], index: number) => {
		return (
			<React.Fragment>
				{children.map((child, index) => {
					return (
						<div key={child.placeId} className={`acc-place ${child.type}`} >
							<div className={`acc-tit ${selectedPlaceIds.includes(child.placeId) ? 'active' : ''}`} onClick={() => handleClick(child.placeId)}>{`${child.name}`}</div>
							<div className="acc-con">
								{selectedPlaceIds.includes(child.placeId) && renderEquips(child.equips)}
								{child.children && checkForRenderPlaces(child.children)}
							</div>
						</div>
					);
				})}
			</React.Fragment>
		);
	};

	//! handler
	const handleChangeForActValue = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number(event.target.value);
		setSelectedActuatorList(prev => prev.map((actuator, i) => i === index ? { ...actuator, value } : actuator));
	};

	const handleChangeForActValueByTagClassAct = (index: number, event: React.ChangeEvent<HTMLButtonElement>) => {
		const value = Number(event.target.value);
		setSelectedActuatorList(prev => prev.map((actuator, i) => i === index ? { ...actuator, value } : actuator));
		return value
	};

	const handleActuatorSetting = (actuator: ActuatorType, index?: number) => (event: any) => {

		// ! 수정 시작

		if (index !== undefined) {
			let value = handleChangeForActValueByTagClassAct(index, event)

			setActuatorUpdateData(prev => ({
				...prev,
				params: [
					{
						id: actuator.tagId,
						classCode: actuator.tagClass.code,
						address: actuator.gatewayMap?.map || '',	// 현재 값이 없음
						encoding: actuator.tagClass.encoding,
						value,
						seq: prev.params[0].seq,
					},
				],
			}));
		} else {
			// 해당 placeId의 equip 정보를 가져와야함.
			setActuatorUpdateData(prev => ({
				...prev,
				params: [
					{
						id: actuator.tagId,
						classCode: actuator.tagClass.code,
						address: actuator.gatewayMap?.map || '',	// 현재 값이 없음
						encoding: actuator.tagClass.encoding,
						value: actuator.value,
						seq: prev.params[0].seq,
					},
				],
			}));
		}

		// ! 수정 끝
	};


	// const handleActuatorSetting = (actuator: ActuatorType) => (event: any) => {

	// 	// 해당 placeId의 equip 정보를 가져와야함.
	// 	setActuatorUpdateData(prev => ({
	// 		...prev,
	// 		params: [
	// 			{
	// 				id: actuator.tagId,
	// 				classCode: actuator.tagClass.code,
	// 				address: actuator.gatewayMap.map,
	// 				encoding: actuator.tagClass.encoding,
	// 				value: actuator.value,
	// 				seq: prev.params[0].seq,
	// 			},
	// 		],
	// 	}));
	// };

	useEffect(() => {
		if (actuatorUpdateData) {
			updateActuator().then((data) => {
				setIsLoading(null);


				console.log("data :: ", data);

				if (!data) return;

				if (data?.data === 200) {
					confirmAlert({
						title: "제어값이 설정되었습니다.",
						buttons: [{ label: "확인" }],
						overlayClassName: "react-confirm-alert-overlay"
					});

					// monitoringData의 해당 값 동기화
					getMonitoring().then((data) => {
						setMonitoringData([data]);
					});

				} else {
					confirmAlert({
						title: "제어값 설정에 실패하였습니다.",
						buttons: [{ label: "확인" }],
						overlayClassName: "react-confirm-alert-overlay"
					});

					// 값 초기화
					const beforeActuatorValueElement = document.getElementsByName("original-value-" + isLoading).item(0);
					const beforeActuatorValue = beforeActuatorValueElement ? beforeActuatorValueElement.getAttribute("value") : null;

					setSelectedActuatorList(prev => prev.map((actuator) => {
						return {
							...actuator,
							value: beforeActuatorValue !== null ? Number(beforeActuatorValue) : actuator.value,
						};
					}));

				};
			});
		}
	}, [actuatorUpdateData, updateActuator])


	return (
		<Container>
			<PageTop>
				<p className="pagetit">모니터링</p>
				<p className="move-btn">
					<img src="./img/X.svg" onClick={() => { navigate(-1) }} alt="X" />
				</p>
			</PageTop>


			<styles.MonitoringData>
				<div className="last-update">
					<p>최근 업데이트 : {updateTime}</p>
				</div>

				<div className="monitoring-data-wrap">
					{monitoringData && monitoringData.length > 0 && renderPlaces(monitoringData, 0)}
				</div>

			</styles.MonitoringData>

			<Modal open={open} onClose={handleClose}>
				<styles.MonitoringData style={{ display: 'block', padding: '0' }}>

					<Box sx={{
						position: 'fixed' as 'fixed',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: '80vw',
						minWidth: '340px',
						height: '80vh',
						bgcolor: 'background.paper',
						border: '0',
						borderRadius: 2,
						boxShadow: 24,
						padding: '2em',
						paddingTop: '1em',
						overflow: 'auto',
					}}>
						<PageTop style={{ marginBottom: '20px' }}>
							<p className="pagetit">제어장치 리스트</p>
							<p className="move-btn">
								<img src="./img/X.svg" onClick={handleClose} alt="X" />
							</p>
						</PageTop>

						{selectedActuatorList
							&& selectedActuatorList.length > 0
							&& (
								<ul className="actuator-setting" >
									{selectedActuatorList.map((actuator: ActuatorType, index: number) => {
										const renderActionsButtons = (actuator: ActuatorType, index: number) => {
											return actuator.tagClass.tagClassActions.map((action: any, actionIndex: number) => (
												<button value={action.value} key={action.value} type="button" onClick={handleActuatorSetting(actuator, index)} style={{ marginRight: '10px' }}>
													{action.name}
												</button>
											));
										};

										const renderInputs = (actuator: ActuatorType) => {
											if (actuator.tagClass.tagClassActions && actuator.tagClass.tagClassActions.length > 0) {

												// console.log("actuator :: ", actuator);
												// console.log("actuator.tagClass.tagClassActions :: ", actuator.tagClass.tagClassActions);

												
												 // 조건에 맞는 값을 찾고, 없는 경우 undefined로 처리
												let foundAction = actuator.tagClass.tagClassActions.find((ele: any) => ele.value == actuator.value);
												// console.log("test :: ", actuator.tagClass.tagClassActions.find((ele: any) => ele.value == actuator.value).name);
												// console.log("foundAction :: ", foundAction);
												let value = foundAction ? foundAction.name : '';

												return (
													<div className="act-con">
														<input type="text" value={actuator.value} name={"original-value-" + actuator.tagId.toString()} disabled style={{ display: 'none' }} />
														<input type="text" value={value} onChange={handleChangeForActValue(index)} /> <span>{actuator.tagClass.unit}</span>
													</div>
												)
											} else {
												return (
													<div className="act-con">
														<input type="number" value={actuator.value} name={"original-value-" + actuator.tagId.toString()} disabled style={{ display: 'none' }} />
														<input type="number" value={actuator.value} onChange={handleChangeForActValue(index)} /> <span>{actuator.tagClass.unit}</span>
													</div>
												)
											}

										}

										// ! 수정 시작
										if (actuator.tagClass.tagClassActions && actuator.tagClass.tagClassActions.length > 0) {

											return (
												<li key={index} className="actuator-setting-list">
													<div className="top">
														<div className="act-tit">{actuator.name}</div>
														{/* <div className="act-con">
															<input type="text" value={actuator.value} name={"original-value-" + actuator.tagId.toString()} disabled style={{ display: 'none' }} />
															<input type="text" value={actuator.tagClass.tagClassActions[0].name} onChange={handleChangeForActValue(index)} /> <span>{actuator.tagClass.unit}</span>
														</div> */}

														{
															renderInputs(actuator)
														}
													</div>

													<div className="bottom">
														{
															// !actuator.tagClass.tagClassActions.length의 길이 만큼 버튼 만들기
															// isLoading === actuator.tagId ?
															// 	<CircularProgress size={30} />
															// 	: <button type="button" onClick={handleActuatorSetting(actuator)}>설정</button>
															isLoading === actuator.tagId ?
																<CircularProgress size={30} /> : renderActionsButtons(actuator, index)
														}

													</div>
												</li>
											)
										} else {
											return (
												<li key={index} className="actuator-setting-list">
													<div className="top">
														<div className="act-tit">{actuator.name}</div>
														{/* <div className="act-con">
															<input type="number" value={actuator.value} name={"original-value-" + actuator.tagId.toString()} disabled style={{ display: 'none' }} />
															<input type="number" value={actuator.value} onChange={handleChangeForActValue(index)} /> <span>{actuator.tagClass.unit}</span>
														</div> */}


														{
															renderInputs(actuator)
														}
													</div>

													<div className="bottom">
														{
															isLoading === actuator.tagId ?
																<CircularProgress size={30} />
																: <button type="button" onClick={handleActuatorSetting(actuator)}>설정</button>
														}

													</div>
												</li>
											)
										}



									})}
								</ul>
							)
						}
					</Box>
				</styles.MonitoringData>
			</Modal>
		</Container>
	);
};

export default MonitoringPage;


const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	li{
		list-style: none;
	}
`;

const PageTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #dddddd;
  background-color: #fff;
  padding: 15px 0;
  font-size: 1.13em;
  position: relative;
  .pagetit {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 2.5vw;
    font-family: "spoqa500";
  }
  .move-btn {
    position: absolute;
    height: 100%;
    top: 0;
    right: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    & img {
      cursor: pointer;
      width: 100%;
    }
  }


  @media screen and (min-width: 1050px) {
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid #dddddd;
    background-color: #fff;
    padding: 20px 0;
    font-size: 1.4em;
    position: relative;

    .move-btn {
      position: absolute;
      width: 1.5%;
      height: 100%;
      top: 0;
      right: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      & img {
        cursor: pointer;
        width: 100%;
      }
    }
  }
`;
